import React, { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Alert,
  Tabs,
  Progress,
  Space,
  Button,
  Typography,
  Spin,
  Empty,
  DatePicker,
  InputNumber,
  Divider,
} from "antd";
import {
  WarningOutlined,
  InboxOutlined,
  SwapOutlined,
  AlertOutlined,
  ShoppingCartOutlined,
  FileProtectOutlined,
  RightOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import moment from "moment";
import {
  getShortages,
  getExcessStock,
  getWastageAnalytics,
  getMaterialConsumption,
  getAllGRNs,
  getAllStock,
  adjustStock,
} from "../../api/inventoryApi";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

const InventoryDashboard = () => {
  // ── Auth ───────────────────────────────────────────────────
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");

  // ── State ──────────────────────────────────────────────────
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [shortages, setShortages] = useState(null);
  const [excessData, setExcessData] = useState(null);
  const [wastageAnalytics, setWastageAnalytics] = useState(null);
  const [consumption, setConsumption] = useState(null);
  const [recentGRNs, setRecentGRNs] = useState([]);
  const [paperStock, setPaperStock] = useState([]);
  const [actualWeights, setActualWeights] = useState({});
  const [dateRange, setDateRange] = useState([
    moment().subtract(30, "days"),
    moment(),
  ]);

  // ── Data Loading ───────────────────────────────────────────
  useEffect(() => {
    fetchAll();
  }, []);

  // ── Auth guard (after hooks) ───────────────────────────────
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }
  checkTokenExp(accessToken);

  const fetchAll = async () => {
    setLoading(true);
    const fromDate = dateRange[0].format("YYYY-MM-DD");
    const toDate = dateRange[1].format("YYYY-MM-DD");

    const results = await Promise.allSettled([
      getShortages(),
      getExcessStock({ count: 100 }),
      getWastageAnalytics({ start_date: fromDate, end_date: toDate }),
      getMaterialConsumption({ from_date: fromDate, to_date: toDate }),
      getAllGRNs({ count: 5, page: 1 }),
      getAllStock({ category: "PAPER" }),
    ]);

    if (results[0].status === "fulfilled") setShortages(results[0].value);
    if (results[1].status === "fulfilled") setExcessData(results[1].value);
    if (results[2].status === "fulfilled") setWastageAnalytics(results[2].value);
    if (results[3].status === "fulfilled") setConsumption(results[3].value);
    if (results[4].status === "fulfilled")
      setRecentGRNs(results[4].value?.data || []);
    if (results[5].status === "fulfilled") {
      const stock = results[5].value || [];
      // Build paper reel rows sorted by reel_width
      const rows = stock
        .filter((s) => s.reel_width)
        .map((s) => {
          const reelWidth = s.reel_width;
          const numReels = s.total_qty || 0;
          const empiricalWeight = Math.round(14 * reelWidth * numReels * 100) / 100;
          return {
            key: `${s.material?.id}_${reelWidth}`,
            material_id: s.material?.id,
            material_name: s.material?.name || "Unknown",
            reel_width: reelWidth,
            num_reels: numReels,
            empirical_weight: empiricalWeight,
            actual_weight: s.available_weight || null,
            batches: s.batches || [],
          };
        })
        .sort((a, b) => a.reel_width - b.reel_width);
      setPaperStock(rows);
    }

    // Count failures
    const failures = results.filter((r) => r.status === "rejected").length;
    if (failures > 0 && failures < results.length) {
      toast.warning("Some inventory data failed to load");
    } else if (failures === results.length) {
      toast.error("Failed to load inventory dashboard");
    }

    setLoading(false);
  };

  // ── Shortage columns ───────────────────────────────────────
  const shortageColumns = [
    {
      title: "Material",
      key: "material",
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.material?.name || r.material_type}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {r.material?.code} • {r.material_type}
          </Text>
        </Space>
      ),
    },
    {
      title: "RS",
      key: "reel_width",
      width: 70,
      render: (_, r) => r.reel_width ? <Text strong>{r.reel_width}"</Text> : "-",
    },
    {
      title: "Shortage",
      key: "shortage",
      width: 130,
      render: (_, r) => (
        <Text type="danger" strong>
          {r.total_shortage?.toLocaleString()} {r.unit}
        </Text>
      ),
    },
    {
      title: "Affected Jobs",
      key: "jobs",
      width: 130,
      render: (_, r) => (
        <Tag color="orange">{r.affected_jobs?.length || 0} jobs</Tag>
      ),
    },
  ];

  // ── Excess stock columns ───────────────────────────────────
  const excessColumns = [
    {
      title: "Source Job",
      key: "source",
      render: (_, r) => (
        <Text>{r.source_job?.job_number || "-"}</Text>
      ),
    },
    {
      title: "Stage",
      key: "stage",
      render: (_, r) => r.stage?.name || "-",
    },
    {
      title: "Specs",
      key: "specs",
      render: (_, r) => (
        <Space size={4} wrap>
          {r.ply && <Tag>{r.ply} Ply</Tag>}
          {r.roll_size && <Tag>RS {r.roll_size}&quot;</Tag>}
          {r.cutting_size && <Tag>CS {r.cutting_size}&quot;</Tag>}
        </Space>
      ),
    },
    {
      title: "Available",
      key: "qty",
      width: 110,
      render: (_, r) => (
        <Text strong style={{ color: r.available_qty > 0 ? "#52c41a" : "#999" }}>
          {r.available_qty?.toLocaleString()}
        </Text>
      ),
    },
  ];

  // ── GRN columns ────────────────────────────────────────────
  const grnColumns = [
    {
      title: "GRN #",
      dataIndex: "grn_number",
      key: "grn_number",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => {
        const color = { draft: "default", submitted: "processing", approved: "success", rejected: "error" }[s] || "default";
        return <Tag color={color}>{s}</Tag>;
      },
    },
    {
      title: "Accepted",
      dataIndex: "accepted_quantity",
      key: "accepted",
      render: (v) => v?.toLocaleString() || 0,
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "date",
      render: (d) => (d ? moment(d).format("DD MMM") : "-"),
    },
  ];

  // ── Consumption columns ────────────────────────────────────
  const consumptionColumns = [
    {
      title: "Material",
      key: "material",
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text>{r.material_name}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {r.material_code}
          </Text>
        </Space>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 100,
      render: (c) => <Tag>{c}</Tag>,
    },
    {
      title: "Consumed",
      key: "consumed",
      width: 120,
      render: (_, r) => (
        <Text strong>
          {r.total_consumed?.toLocaleString()} {r.unit}
        </Text>
      ),
    },
    {
      title: "Jobs",
      dataIndex: "jobs_used_in",
      key: "jobs",
      width: 70,
    },
  ];

  if (loading) {
    return (
      <>
        <PageTitle title="Back" subtitle="Inventory Management" />
        <div style={{ textAlign: "center", padding: 80 }}>
          <Spin size="large" />
        </div>
      </>
    );
  }

  const totalShortages = shortages?.total_shortages || 0;
  const totalExcess = excessData?.total || 0;
  const totalWastage = wastageAnalytics?.totals?._sum?.wastage_quantity || 0;
  const avgWastagePct = wastageAnalytics?.totals?._avg?.wastage_pct || 0;

  return (
    <>
      <PageTitle title="Back" subtitle="Inventory Management" />

      {/* Critical alert banner */}
      {totalShortages > 0 && (
        <Alert
          type="error"
          showIcon
          icon={<AlertOutlined />}
          message="Material Shortages Detected"
          description={`${totalShortages} material shortage${totalShortages > 1 ? "s" : ""} across production jobs require attention.`}
          style={{ marginBottom: 16 }}
          action={
            <Button
              size="small"
              type="primary"
              danger
              onClick={() => navigate("/inventory/shortages")}
            >
              View Shortages
            </Button>
          }
        />
      )}

      {/* Paper Reel Stock */}
      <Card
        title={<Space><InboxOutlined /> Paper Reel Stock</Space>}
        size="small"
        style={{ marginBottom: 16 }}
        extra={
          <Space>
            <Text type="secondary">Empirical Weight = 14 × Reel Width × No. of Reels (Kg)</Text>
            <Button size="small" icon={<ReloadOutlined />} onClick={fetchAll}>Refresh</Button>
          </Space>
        }
      >
        <Table
          size="small"
          dataSource={paperStock}
          rowKey="key"
          pagination={false}
          bordered
          columns={[
            {
              title: "Reel Size (RS)",
              dataIndex: "reel_width",
              width: 110,
              render: (v) => <Text strong>{v}"</Text>,
              sorter: (a, b) => a.reel_width - b.reel_width,
            },
            {
              title: "No. of Reels",
              dataIndex: "num_reels",
              width: 100,
              render: (v) => <Text strong>{v}</Text>,
            },
            {
              title: "Material",
              dataIndex: "material_name",
              width: 200,
            },
            {
              title: "Empirical Weight (Kg)",
              dataIndex: "empirical_weight",
              width: 160,
              render: (v) => <Text type="secondary">{v?.toLocaleString()} Kg</Text>,
            },
            {
              title: "Actual Weight (Kg)",
              dataIndex: "actual_weight",
              width: 160,
              render: (v, record) => {
                const val = actualWeights[record.key] !== undefined ? actualWeights[record.key] : v;
                return (
                  <InputNumber
                    size="small"
                    min={0}
                    value={val || undefined}
                    placeholder={String(record.empirical_weight)}
                    addonAfter="Kg"
                    style={{ width: 140 }}
                    onChange={(newVal) => setActualWeights((prev) => ({ ...prev, [record.key]: newVal }))}
                  />
                );
              },
            },
            {
              title: "Effective Weight",
              width: 130,
              render: (_, record) => {
                const aw = actualWeights[record.key] !== undefined ? actualWeights[record.key] : record.actual_weight;
                const effective = aw || record.empirical_weight;
                return <Text strong style={{ color: aw ? "#52c41a" : "#faad14" }}>{effective?.toLocaleString()} Kg</Text>;
              },
            },
          ]}
          summary={() => {
            const totalReels = paperStock.reduce((s, r) => s + r.num_reels, 0);
            const totalEmpirical = paperStock.reduce((s, r) => s + r.empirical_weight, 0);
            const totalEffective = paperStock.reduce((s, r) => {
              const aw = actualWeights[r.key] !== undefined ? actualWeights[r.key] : r.actual_weight;
              return s + (aw || r.empirical_weight);
            }, 0);
            return (
              <Table.Summary.Row style={{ fontWeight: "bold", background: "#fafafa" }}>
                <Table.Summary.Cell>TOTAL</Table.Summary.Cell>
                <Table.Summary.Cell>{totalReels}</Table.Summary.Cell>
                <Table.Summary.Cell />
                <Table.Summary.Cell>{totalEmpirical?.toLocaleString()} Kg</Table.Summary.Cell>
                <Table.Summary.Cell />
                <Table.Summary.Cell>{totalEffective?.toLocaleString()} Kg</Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
      </Card>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable onClick={() => navigate("/inventory/shortages")}>
            <Statistic
              title="Material Shortages"
              value={totalShortages}
              prefix={<WarningOutlined style={{ color: "#ff4d4f" }} />}
              valueStyle={{ color: totalShortages > 0 ? "#ff4d4f" : "#52c41a" }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {shortages?.aggregated?.length || 0} unique materials
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable onClick={() => navigate("/inventory/excess")}>
            <Statistic
              title="Excess / WIP Stock"
              value={totalExcess}
              prefix={<InboxOutlined style={{ color: "#1890ff" }} />}
              suffix="items"
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Semi-processed inventory
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable onClick={() => navigate("/inventory/grn")}>
            <Statistic
              title="Recent GRNs"
              value={recentGRNs.length}
              prefix={<FileProtectOutlined style={{ color: "#52c41a" }} />}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Goods receipts
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Wastage (period)"
              value={totalWastage.toLocaleString()}
              prefix={<SwapOutlined style={{ color: "#faad14" }} />}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Avg {avgWastagePct.toFixed(1)}% wastage rate
            </Text>
          </Card>
        </Col>
      </Row>

      {/* Tabs: Shortages | WIP | Consumption | GRNs */}
      <Card
        extra={
          <Space>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates) setDateRange(dates);
              }}
              allowClear={false}
            />
            <Button icon={<ReloadOutlined />} onClick={fetchAll}>
              Refresh
            </Button>
            <Button onClick={() => navigate("/inventory/history")}>
              Consumption History
            </Button>
            <Button type="primary" onClick={() => navigate("/inventory/settlement")}>
              Settlement
            </Button>
          </Space>
        }
      >
        <Tabs
          defaultActiveKey="shortages"
          items={[
            {
              key: "shortages",
              label: (
                <span>
                  <WarningOutlined />
                  {" "}Shortages ({shortages?.aggregated?.length || 0})
                </span>
              ),
              children: (
                <>
                  {shortages?.aggregated?.length > 0 ? (
                    <Table
                      columns={shortageColumns}
                      dataSource={shortages.aggregated}
                      rowKey="material_id"
                      pagination={false}
                      size="small"
                    />
                  ) : (
                    <Empty description="No material shortages — all requirements fulfilled" />
                  )}
                  <div style={{ marginTop: 12, textAlign: "right" }}>
                    <Button
                      type="link"
                      icon={<RightOutlined />}
                      onClick={() => navigate("/inventory/shortages")}
                    >
                      View full shortage report
                    </Button>
                  </div>
                </>
              ),
            },
            {
              key: "excess",
              label: (
                <span>
                  <InboxOutlined />
                  {" "}WIP / Excess ({totalExcess})
                </span>
              ),
              children: (
                <>
                  {excessData?.excess?.length > 0 ? (
                    <Table
                      columns={excessColumns}
                      dataSource={(excessData.excess || []).slice(0, 10)}
                      rowKey="id"
                      pagination={false}
                      size="small"
                    />
                  ) : (
                    <Empty description="No excess or semi-processed stock" />
                  )}
                  <div style={{ marginTop: 12, textAlign: "right" }}>
                    <Button
                      type="link"
                      icon={<RightOutlined />}
                      onClick={() => navigate("/inventory/excess")}
                    >
                      View all excess stock
                    </Button>
                  </div>
                </>
              ),
            },
            {
              key: "consumption",
              label: (
                <span>
                  <ShoppingCartOutlined />
                  {" "}Consumption
                </span>
              ),
              children: (
                <>
                  {consumption?.consumption?.length > 0 ? (
                    <Table
                      columns={consumptionColumns}
                      dataSource={consumption.consumption}
                      rowKey="material_code"
                      pagination={{ pageSize: 10 }}
                      size="small"
                    />
                  ) : (
                    <Empty description="No consumption data for selected period" />
                  )}
                </>
              ),
            },
            {
              key: "grn",
              label: (
                <span>
                  <FileProtectOutlined />
                  {" "}Recent GRNs
                </span>
              ),
              children: (
                <>
                  <Table
                    columns={grnColumns}
                    dataSource={recentGRNs}
                    rowKey="id"
                    pagination={false}
                    size="small"
                  />
                  <div style={{ marginTop: 12, textAlign: "right" }}>
                    <Button
                      type="link"
                      icon={<RightOutlined />}
                      onClick={() => navigate("/inventory/grn")}
                    >
                      View all GRNs
                    </Button>
                  </div>
                </>
              ),
            },
            {
              key: "wastage",
              label: (
                <span>
                  <SwapOutlined />
                  {" "}Wastage Analysis
                </span>
              ),
              children: <WastageBreakdown data={wastageAnalytics} />,
            },
          ]}
        />
      </Card>
    </>
  );
};

// ── Sub-component: Wastage breakdown ─────────────────────────
const WastageBreakdown = ({ data }) => {
  if (!data || !data.totals) {
    return <Empty description="No wastage data for selected period" />;
  }

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Statistic
            title="Total Wastage"
            value={data.totals._sum?.wastage_quantity || 0}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="Material Cost"
            value={data.totals._sum?.material_cost || 0}
            prefix="₹"
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="Avg Wastage %"
            value={data.totals._avg?.wastage_pct || 0}
            precision={2}
            suffix="%"
            valueStyle={{
              color: (data.totals._avg?.wastage_pct || 0) > 3 ? "#ff4d4f" : "#52c41a",
            }}
          />
        </Col>
        <Col span={6}>
          <Statistic title="Total Entries" value={data.totals._count || 0} />
        </Col>
      </Row>

      {data.by_reason?.length > 0 && (
        <>
          <Text strong style={{ display: "block", marginBottom: 8 }}>
            By Reason Category
          </Text>
          <Table
            dataSource={data.by_reason}
            rowKey="reason_category"
            pagination={false}
            size="small"
            columns={[
              {
                title: "Reason",
                dataIndex: "reason_category",
                render: (v) => (
                  <Tag>{(v || "unknown").replace(/_/g, " ")}</Tag>
                ),
              },
              {
                title: "Wastage Qty",
                render: (_, r) =>
                  (r._sum?.wastage_quantity || 0).toLocaleString(),
                width: 130,
              },
              {
                title: "Count",
                render: (_, r) => r._count || 0,
                width: 80,
              },
            ]}
          />
        </>
      )}
    </div>
  );
};

export default InventoryDashboard;
